import Chart from 'chart.js/auto';
import React, { useEffect, useRef } from 'react';
import DividerWithText from 'style-guide/DividerWithText';
import Text from 'style-guide/Typography/Text';
import { chartColors, getAverageByLabel, hasAnyAnswer } from './helper';

const backgroundColors = [chartColors.firstColor, chartColors.secondColor, chartColors.thirdColor];

const borderColors = [chartColors.firstBorderColor, chartColors.secondBorderColor, chartColors.thirdBorderColor];

const getTooltipNotes = (group, axisLabel) => {
  const relevant = group.feedbackQuestions.filter((q) => q.type === axisLabel);
  return relevant.map((item) => item.type);
};

const ChartComponent = ({ feedbackGroups = [], maxPoint = 7 }) => {
  const radarRef = useRef(null);
  const polarRef = useRef(null);
  const radarInstanceRef = useRef(null);
  const polarInstanceRef = useRef(null);

  const labels = React.useMemo(
    () => [...new Set(feedbackGroups.flatMap((group) => group.feedbackQuestions.map((question) => question.type)))],
    [feedbackGroups]
  );

  const showRadarChart = hasAnyAnswer(feedbackGroups);

  // Radar chart data
  const radarChartData = React.useMemo(
    () => ({
      labels,
      datasets: feedbackGroups.map((group, idx) => ({
        label: group.legend,
        parsing: { key: 'value' },
        data: labels.map((label) => {
          const avg = getAverageByLabel(group.feedbackQuestions, [label])[0];
          return {
            value: avg === null ? null : Math.round(avg * 10) / 10,
            notes: getTooltipNotes(group, label),
          };
        }),
        backgroundColor: backgroundColors[idx % backgroundColors.length],
        borderColor: borderColors[idx % borderColors.length],
        borderWidth: 1,
      })),
    }),
    [labels, feedbackGroups]
  );

  // Polar chart data
  const polarChartData = React.useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: labels.map((label) => {
            const avgArr = feedbackGroups.map((group) => getAverageByLabel(group.feedbackQuestions, [label])[0]);
            const filteredArr = avgArr.filter((val) => val !== null && val !== undefined);
            const avg =
              filteredArr.length > 0
                ? Math.round((filteredArr.reduce((sum, val) => sum + val, 0) / filteredArr.length) * 10) / 10
                : null;
            return avg;
          }),
          borderWidth: 1,
        },
      ],
    }),
    [labels, feedbackGroups]
  );

  useEffect(() => {
    if (!showRadarChart) return;

    // Destroy previous chart instances
    if (radarInstanceRef.current) radarInstanceRef.current.destroy();
    if (polarInstanceRef.current) polarInstanceRef.current.destroy();

    // Radar Chart
    if (radarRef.current) {
      radarInstanceRef.current = new Chart(radarRef.current, {
        type: 'radar',
        data: radarChartData,
        options: {
          responsive: true,
          plugins: {
            legend: { position: 'top' },
            tooltip: {
              callbacks: {
                title: (tooltipItems) => {
                  const point = tooltipItems[0]?.raw;
                  return point?.value === 0 ? '' : tooltipItems[0].label;
                },
                label: (context) => {
                  const datasetLabel = context.dataset.label;
                  const point = context.raw;
                  if (point && point.value === 0 && point.notes?.length) {
                    return [`${datasetLabel}`, ...point.notes.map((note) => `${note}: ${point.value}`)];
                  }
                  if (point && point.value !== 0) {
                    return `${datasetLabel}: ${point.value}`;
                  }
                  return null;
                },
              },
            },
          },
          scales: {
            r: {
              min: 0,
              max: maxPoint,
              ticks: { stepSize: 1, beginAtZero: true },
              pointLabels: {
                font: { size: 12 },
                callback(label) {
                  const maxLength = 20;
                  return label.length > maxLength ? `${label.slice(0, maxLength - 3)}...` : label;
                },
              },
            },
          },
        },
      });
    }

    // Polar Chart
    if (polarRef.current) {
      polarInstanceRef.current = new Chart(polarRef.current, {
        type: 'polarArea',
        data: polarChartData,
        options: {
          responsive: true,
          plugins: {
            legend: { position: 'top' },
          },
          scales: {
            r: {
              min: 0,
              max: maxPoint,
              ticks: { stepSize: 1, beginAtZero: true },
            },
          },
        },
      });
    }
  }, [radarChartData, polarChartData, feedbackGroups, maxPoint, showRadarChart]);

  return showRadarChart ? (
    <div>
      <div className='chart-block'>
        <div className='mt--32'>
          <canvas ref={radarRef} />
        </div>
      </div>
      <DividerWithText />
      <div className='mt--32'>
        <canvas ref={polarRef} />
      </div>
    </div>
  ) : (
    <div className='mt--24 text-align-center survey-text'>
      <Text>The survey hasn't been completed yet. Results will be available once it's submitted.</Text>
      <DividerWithText />
    </div>
  );
};

export default ChartComponent;
